import React from 'react';

/**
 * UploadProgressOverlay
 *
 * Full-screen overlay displayed during form submission while files are uploading.
 * Handles progress, completion, and rollback on error so applicants always know
 * what's happening and no partial/orphaned data remains.
 *
 * Props:
 *   visible      {boolean}  - whether to show the overlay
 *   files        {Array}    - array of { name, progress (0-100), done, error }
 *   overallPct   {number}   - 0-100 overall progress percentage
 *   phase        {string}   - 'uploading' | 'submitting' | 'done' | 'error'
 *   errorMsg     {string}   - Optional error message to display on error
 *   onClose      {Function} - Callback to close/dismiss overlay on error
 */
export default function UploadProgressOverlay({
  visible,
  files = [],
  overallPct = 0,
  phase = 'uploading',
  errorMsg = '',
  onClose,
}) {
  if (!visible) return null;

  const isError = phase === 'error';
  const hasFiles = files.length > 0;

  const phaseLabel = isError
    ? 'Submission & Upload Failed'
    : {
        uploading: 'Uploading files\u2026',
        submitting: 'Submitting application\u2026',
        done: 'Finalising\u2026',
      }[phase] || 'Please wait\u2026';

  const phaseSubLabel = isError
    ? (errorMsg || 'A problem occurred while uploading or submitting. Any partially uploaded files were safely cleaned up.')
    : {
        uploading: 'Please keep this page open. Large files may take several minutes.',
        submitting: 'Sending your completed application to our servers.',
        done: 'Almost there \u2014 wrapping up your submission.',
      }[phase] || '';

  return (
    <div style={styles.overlay}>
      <div style={isError ? { ...styles.card, ...styles.cardError } : styles.card}>
        {/* SVG gradient definition (must be inline to work cross-browser) */}
        <svg width="0" height="0" style={{ position: 'absolute' }}>
          <defs>
            <linearGradient id="spinGrad" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#6366f1" />
              <stop offset="100%" stopColor="#a855f7" />
            </linearGradient>
            <linearGradient id="errorGrad" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#ef4444" />
              <stop offset="100%" stopColor="#dc2626" />
            </linearGradient>
          </defs>
        </svg>

        {/* Animated icon or error badge */}
        {isError ? (
          <div style={styles.iconWrapError}>
            <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
          </div>
        ) : (
          <div style={styles.iconWrap}>
            <svg style={styles.spinnerRing} viewBox="0 0 50 50">
              <circle
                style={styles.spinnerCircle}
                cx="25"
                cy="25"
                r="20"
                fill="none"
                stroke="url(#spinGrad)"
                strokeWidth="4"
                strokeLinecap="round"
                strokeDasharray="80 40"
              />
            </svg>
            <div style={styles.iconInner}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="16 16 12 12 8 16" />
                <line x1="12" y1="12" x2="12" y2="21" />
                <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3" />
              </svg>
            </div>
          </div>
        )}

        <h2 style={isError ? { ...styles.title, color: '#fca5a5' } : styles.title}>{phaseLabel}</h2>
        <p style={isError ? { ...styles.subtitle, color: '#e2e8f0' } : styles.subtitle}>{phaseSubLabel}</p>

        {isError && (
          <div style={styles.rollbackBanner}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#34d399" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginRight: '8px' }}>
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
              <polyline points="9 12 11 14 15 10" />
            </svg>
            <span>Partial data cleaned up: No incomplete application or orphaned files remain.</span>
          </div>
        )}

        {/* Overall progress bar (hidden in error unless files are present) */}
        {!isError && (
          <>
            <div style={styles.overallBarTrack}>
              <div style={{ ...styles.overallBarFill, width: `${Math.max(2, overallPct)}%` }} />
            </div>
            <div style={styles.overallPct}>{Math.round(overallPct)}%</div>
          </>
        )}

        {/* Per-file breakdown */}
        {hasFiles && (
          <div style={styles.fileList}>
            {files.map((f, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <div key={i} style={styles.fileRow}>
                <div style={styles.fileInfo}>
                  <span style={styles.fileName} title={f.name}>{f.name}</span>
                  <span style={f.error ? styles.fileStatusError : f.done ? styles.fileStatusDone : styles.fileStatusPending}>
                    {f.error ? '\u2717 Failed' : f.done ? '\u2713 Done' : `${Math.round(f.progress || 0)}%`}
                  </span>
                </div>
                <div style={styles.fileBarTrack}>
                  <div
                    style={{
                      ...styles.fileBarFill,
                      width: `${Math.max(0, Math.min(100, f.progress || 0))}%`,
                      background: f.error ? '#ef4444' : f.done ? '#22c55e' : 'linear-gradient(90deg,#6366f1,#8b5cf6)',
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        )}

        {isError ? (
          <div style={styles.actionWrap}>
            <button
              type="button"
              style={styles.dismissBtn}
              onClick={onClose}
            >
              Dismiss & Review Form
            </button>
          </div>
        ) : (
          <p style={styles.warning}>\u26a0\ufe0f Do not close or refresh this page</p>
        )}
      </div>

      <style>{`
        @keyframes upload-overlay-spin { to { transform: rotate(360deg); } }
        @keyframes upload-overlay-pulse { 0%,100%{opacity:1} 50%{opacity:0.7} }
      `}</style>
    </div>
  );
}

const styles = {
  overlay: {
    position: 'fixed', inset: 0, zIndex: 9999,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: 'rgba(15,15,30,0.85)',
    backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)',
  },
  card: {
    background: 'linear-gradient(145deg,#1e1b4b,#1e1e3f)',
    border: '1px solid rgba(99,102,241,0.3)',
    borderRadius: '20px', padding: '40px 36px 32px',
    width: '100%', maxWidth: '520px', margin: '16px',
    boxShadow: '0 25px 60px rgba(0,0,0,0.5),0 0 0 1px rgba(99,102,241,0.15)',
    display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center',
    position: 'relative',
  },
  cardError: {
    border: '1px solid rgba(239,68,68,0.4)',
    boxShadow: '0 25px 60px rgba(0,0,0,0.6),0 0 0 1px rgba(239,68,68,0.25)',
  },
  iconWrap: {
    position: 'relative', width: '64px', height: '64px', marginBottom: '20px',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  iconWrapError: {
    width: '64px', height: '64px', marginBottom: '18px',
    borderRadius: '50%', background: 'rgba(239,68,68,0.12)',
    border: '1px solid rgba(239,68,68,0.35)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  spinnerRing: {
    position: 'absolute', inset: 0, width: '64px', height: '64px',
    animation: 'upload-overlay-spin 1.2s linear infinite',
  },
  spinnerCircle: { strokeLinecap: 'round' },
  iconInner: { color: '#a5b4fc', position: 'relative', zIndex: 1 },
  title: { margin: '0 0 6px', fontSize: '1.25rem', fontWeight: 700, color: '#e0e7ff', letterSpacing: '-0.01em' },
  subtitle: { margin: '0 0 20px', fontSize: '0.875rem', color: '#94a3b8', lineHeight: 1.5, maxWidth: '380px' },
  rollbackBanner: {
    display: 'flex', alignItems: 'center', textAlign: 'left',
    background: 'rgba(16,185,129,0.12)', border: '1px solid rgba(16,185,129,0.25)',
    borderRadius: '10px', padding: '10px 14px', marginBottom: '20px',
    fontSize: '0.78rem', color: '#a7f3d0', width: '100%', lineHeight: 1.4,
  },
  overallBarTrack: {
    width: '100%', height: '10px', background: 'rgba(99,102,241,0.15)',
    borderRadius: '99px', overflow: 'hidden', marginBottom: '8px',
  },
  overallBarFill: {
    height: '100%', background: 'linear-gradient(90deg,#6366f1,#a855f7)',
    borderRadius: '99px', transition: 'width 0.4s ease',
    animation: 'upload-overlay-pulse 2s ease-in-out infinite',
  },
  overallPct: { fontSize: '0.8rem', fontWeight: 600, color: '#a5b4fc', marginBottom: '20px' },
  fileList: {
    width: '100%', display: 'flex', flexDirection: 'column', gap: '12px',
    marginBottom: '20px', maxHeight: '200px', overflowY: 'auto',
  },
  fileRow: { textAlign: 'left' },
  fileInfo: { display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '4px' },
  fileName: {
    fontSize: '0.78rem', color: '#cbd5e1',
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '320px',
  },
  fileStatusPending: { fontSize: '0.75rem', color: '#818cf8', fontWeight: 600, flexShrink: 0, marginLeft: '8px' },
  fileStatusDone:    { fontSize: '0.75rem', color: '#4ade80', fontWeight: 600, flexShrink: 0, marginLeft: '8px' },
  fileStatusError:   { fontSize: '0.75rem', color: '#f87171', fontWeight: 700, flexShrink: 0, marginLeft: '8px' },
  fileBarTrack: {
    width: '100%', height: '5px', background: 'rgba(255,255,255,0.07)',
    borderRadius: '99px', overflow: 'hidden',
  },
  fileBarFill: { height: '100%', borderRadius: '99px', transition: 'width 0.3s ease' },
  warning: { fontSize: '0.75rem', color: '#fbbf24', margin: 0, fontWeight: 500 },
  actionWrap: { width: '100%', marginTop: '6px' },
  dismissBtn: {
    width: '100%', padding: '11px 18px', borderRadius: '10px',
    background: 'linear-gradient(135deg, #4f46e5, #7c3aed)',
    border: 'none', color: '#ffffff', fontSize: '0.875rem', fontWeight: 600,
    cursor: 'pointer', boxShadow: '0 4px 14px rgba(79,70,229,0.4)',
    transition: 'all 0.2s ease',
  },
};
